import React from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';

import { Box, Button, Divider, Typography } from '@mui/material';

import { CheckCircleOutlined as CompletedIcon } from '@mui/icons-material';

import styles from './styles';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  title: string;
  nextLabel?: string;
  previousLabel?: string;
  completeLabel?: string;
  steps: CustomStepperStep[];
  step: number;
  subStep?: number;
  isNextDisabled?: boolean;
  handleStepClick: (index: number) => void;
  handleSubStepClick?: (stepIndex: number, subStepIndex: number) => void;
  handleNext?: () => void;
  handlePrevious?: () => void;
  handleComplete?: () => void;
}

interface SubStep {
  label: string;
  detail?: string | null;
}

interface CustomStepperStep {
  label: string;
  detail?: string | null;
  nextLabel?: string;
  handleNext?: () => void;
  subSteps?: SubStep[];
}

const ColumnStepper = ({
  classes,
  title,
  steps,
  step = 0,
  subStep = 0,
  isNextDisabled = false,
  nextLabel = 'Next',
  previousLabel = 'Previous',
  completeLabel = 'Complete',
  handleStepClick,
  handleSubStepClick,
  handleNext,
  handlePrevious,
  handleComplete,
}: Props): React.ReactElement => {
  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="h5">
        {title}
      </Typography>
      <Divider />
      {steps.map((stepItem, stepIndex) => {
        const hasSubSteps = !!stepItem.subSteps?.length;
        return (
          <div key={stepItem.label}>
            <div className={`${classes.step} ${stepIndex < step ? classes.completed : ''}`} onClick={() => (stepIndex < step ? handleStepClick(stepIndex) : null)}>
              <Box sx={{ width: '100%' }} className={classes.label}>
                <Typography className={stepIndex === step && !hasSubSteps ? classes.active : ''} color={hasSubSteps ? 'grey.600' : 'text.primary'}>
                  {stepItem.label}
                </Typography>
                {stepItem.detail && (
                  <Typography overflow={'hidden'} whiteSpace={'nowrap'} maxWidth={'100%'} textOverflow={'ellipsis'} className={classes.detail}>
                    {stepItem.detail}
                  </Typography>
                )}
              </Box>
              {!hasSubSteps && stepIndex < step && (
                <div className={classes.completedIcon}>
                  <CompletedIcon />
                </div>
              )}
            </div>

            {/* Substeps */}
            {stepItem.subSteps && (
              <Box ml={3}>
                {stepItem.subSteps.map((s, subIndex) => (
                  <Box
                    key={s.label}
                    p={(theme) => `${theme.spacing(1.5)} ${theme.spacing(2)} ${theme.spacing(1.5)} ${theme.spacing(1)} !important`}
                    className={`${classes.step} ${stepIndex <= step && subIndex < subStep ? classes.completed : ''} ${
                      stepIndex === step && subIndex === subStep ? classes.active : ''
                    }`}
                    onClick={() => (stepIndex <= step && subIndex < subStep ? handleSubStepClick?.(stepIndex, subIndex) : null)}>
                    <Box className={classes.label}>
                      <Typography fontSize={16} color={stepIndex === step && subIndex === subStep ? 'primary' : 'textSecondary'}>
                        {s.label}
                      </Typography>
                      {!!s.detail && <Typography className={classes.detail}>{s.detail}</Typography>}
                    </Box>
                    {(stepIndex < step || (stepIndex === step && subIndex < subStep)) && (
                      <div className={classes.completedIcon}>
                        <CompletedIcon />
                      </div>
                    )}
                  </Box>
                ))}
              </Box>
            )}
            <Divider />
          </div>
        );
      })}

      <div className={classes.actions}>
        {handleNext && step + 1 < steps.length && (
          <Button className={classes.action} data-qa="next-step" variant="contained" color="primary" onClick={steps[step].handleNext || handleNext} disabled={isNextDisabled}>
            {steps[step].nextLabel || nextLabel}
          </Button>
        )}
        {handleComplete && step + 1 === steps.length && (
          <Button className={classes.action} data-qa="complete-step" variant="contained" color="primary" onClick={handleComplete}>
            {completeLabel}
          </Button>
        )}
        {handlePrevious && step > 0 && (
          <Button className={classes.action} data-qa="previous-step" variant="outlined" color="primary" onClick={handlePrevious}>
            {previousLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default withStyles(styles)(ColumnStepper);
